import React from 'react'
import { IonContent, IonIcon, IonPage } from '@ionic/react'
import { chevronBackOutline } from 'ionicons/icons'
import { useHistory } from 'react-router-dom'
import styled from '@emotion/styled'
import logoBlue from '../assets/auth/logoBlue.svg'
import logoBlueMobile from '../assets/auth/logoBlue-mobile.svg'
import { AUTH_COLORS, AUTH_MOBILE_BREAKPOINT } from './AuthForm'

const NEW_DRIVER_URL = 'https://newdriver.com.br'
const TERMOS_USO_URL = 'https://hyperpay.blob.core.windows.net/noticias/TERMO-DE-USO-NEW-DRIVER.pdf'
const POLITICA_PRIVACIDADE_URL = 'https://newdriver.com.br/politica-privacidade'

type AuthPageLayoutVariant = 'login' | 'recovery'

type AuthPageLayoutProps = {
  children: React.ReactNode
  variant?: AuthPageLayoutVariant
}

const AUTH_LAYOUT_VARIANTS: Record<
  AuthPageLayoutVariant,
  { mobileTitle: string; backAction: 'site' | 'login' }
> = {
  login: {
    mobileTitle: 'Acessar',
    backAction: 'site',
  },
  recovery: {
    mobileTitle: 'Recuperar senha',
    backAction: 'login',
  },
}

export function AuthPageLayout({ children, variant = 'login' }: AuthPageLayoutProps) {
  const history = useHistory()
  const layoutVariant = AUTH_LAYOUT_VARIANTS[variant]

  const handleBack = () => {
    if (layoutVariant.backAction === 'login') {
      history.push('/')
      return
    }

    window.location.href = NEW_DRIVER_URL
  }

  return (
    <IonPage>
      <IonContent>
        <AuthPageShell>
          <AuthDesktopHeader onBack={handleBack} />
          <AuthMobileHeader title={layoutVariant.mobileTitle} onBack={handleBack} />
          <AuthCertification mobile />
          <AuthMain>{children}</AuthMain>
          <AuthFooter />
        </AuthPageShell>
      </IonContent>
    </IonPage>
  )
}

function AuthDesktopHeader({ onBack }: { onBack: () => void }) {
  return (
    <DesktopHeaderRoot>
      <DesktopLogo>
        <img src={logoBlue} alt="New Driver" />
      </DesktopLogo>
      <AuthCertification />
      <DesktopBack type="button" onClick={onBack}>
        Voltar
      </DesktopBack>
    </DesktopHeaderRoot>
  )
}

function AuthMobileHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <MobileHeaderRoot>
      <button type="button" onClick={onBack} aria-label="Voltar">
        <IonIcon icon={chevronBackOutline} />
      </button>
      <strong>{title}</strong>
      <img src={logoBlueMobile} alt="New Driver" />
    </MobileHeaderRoot>
  )
}

function AuthCertification({ mobile = false }: { mobile?: boolean }) {
  const Root = mobile ? MobileCertificationRoot : CertificationRoot

  return (
    <Root>
      <strong>
        Ambiente de Aprendizado Homologado pela <span>SENATRAN</span>.
      </strong>
      <small>Resolução CONTRAN n° 1.020/2025 e da Portaria SENATRAN n° 923/2025</small>
    </Root>
  )
}

function AuthMain({ children }: { children: React.ReactNode }) {
  return <MainRoot>{children}</MainRoot>
}

export function AuthFooter() {
  return (
    <FooterRoot>
      Copyright © 2026 A New Driver é uma marca registrada New Driver LTDA | CNPJ:
      31.665.008/0001-09. Todos os direitos reservados.{' '}
      <a href={TERMOS_USO_URL} target="_blank" rel="noopener noreferrer">
        Termos de uso
      </a>{' '}
      e{' '}
      <a href={POLITICA_PRIVACIDADE_URL} target="_blank" rel="noopener noreferrer">
        Política de privacidade
      </a>
    </FooterRoot>
  )
}

const AuthPageShell = styled.div`
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: #ffffff;
  color: ${AUTH_COLORS.blue};
  font-family: Rubik, sans-serif;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    grid-template-rows: auto auto minmax(0, 1fr) auto;
  }
`

const DesktopHeaderRoot = styled.header`
  width: 100%;
  min-height: 74px;
  padding: 24px 30px;
  border-bottom: 1px solid rgba(235, 235, 235, 1);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    display: none;
  }
`

const DesktopLogo = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 91px;
    height: auto;
    display: block;
  }
`

const DesktopBack = styled.button`
  justify-self: end;
  border: 0;
  padding: 0;
  background: transparent;
  color: rgba(18, 42, 65, 0.5);
  font-family: Rubik, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 26px;
  cursor: pointer;
  transition:
    color 0.2s ease,
    opacity 0.2s ease;

  :hover {
    color: ${AUTH_COLORS.orange};
    opacity: 1;
  }
`

const MobileHeaderRoot = styled.header`
  display: none;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    padding: 22px 20px;
    border-bottom: 1px solid rgba(235, 235, 235, 1);
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;

    button {
      width: 25px;
      height: 25px;
      border: 0;
      padding: 0;
      background: transparent;
      color: ${AUTH_COLORS.blue};
      display: flex;
      align-items: center;
      justify-content: flex-start;
      cursor: pointer;
    }

    ion-icon {
      width: 20px;
      height: 20px;
    }

    strong {
      color: ${AUTH_COLORS.blue};
      font-size: 16px;
      font-weight: 500;
      line-height: 26px;
    }

    img {
      justify-self: end;
      width: 28px;
      height: auto;
      display: block;
    }
  }
`

const CertificationRoot = styled.div`
  text-align: center;
  color: ${AUTH_COLORS.blue};

  strong {
    display: block;
    font-size: 16px;
    font-weight: 600;
    line-height: 16px;
  }

  span {
    display: inline-block;
    background-image: linear-gradient(90deg, #bad010 17.79%, #048e7d 80.77%);
    background-clip: text;
    -webkit-background-clip: text;
    color: #048e7d;
    -webkit-text-fill-color: transparent;
  }

  small {
    display: block;
    margin-top: 4px;
    color: #5a6a7a;
    font-size: 10px;
    line-height: 10px;
  }
`

const MobileCertificationRoot = styled(CertificationRoot)`
  display: none;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    display: block;
    padding: 20px 10px 0;
    text-align: center;
    letter-spacing: 0;

    strong {
      font-size: 14px;
      line-height: 14px;
    }

    small {
      margin-top: 6px;
      font-size: 11px;
      line-height: 11px;
    }
  }
`

const MainRoot = styled.main`
  width: 100%;
  min-height: 0;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    padding: 40px 24px;
  }

  @media (max-width: 640px) {
    padding: 50px 16px;
  }
`

const FooterRoot = styled.footer`
  width: 100%;
  padding: 0 22px 38px;
  color: ${AUTH_COLORS.blue};
  font-size: 14px;
  line-height: 26px;
  text-align: center;

  a {
    color: ${AUTH_COLORS.blue};
    text-decoration: underline;
  }

  a:hover {
    color: ${AUTH_COLORS.orange};
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    padding: 0 22px 26px;
    line-height: 14px;
  }
`
